import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { BankAccountService } from '../../services/bankAccount.service';
import { PERMISSIONS } from '../../constants/permissions';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import { formatMoney, humanizeEnum } from '../../utils/format';

/** Bank accounts list (T8, frontend part 1). Columns: Bank / Account # / Branch / GL account. */
const BankAccountList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.BANK_ACCOUNT_MANAGE);
  const institutionId = AuthService.getSelectedInstitution()!;

  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const res = await BankAccountService.listByInstitution(institutionId);
      setAccounts(res);
    } catch (e) {
      toast.showApiError(e, 'Failed to load bank accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(a =>
      a.bankName.toLowerCase().includes(q)
      || a.accountNumber.toLowerCase().includes(q)
      || (a.branchName ?? '').toLowerCase().includes(q));
  }, [accounts, search]);

  return (
    <Box data-help-page="banking.accounts.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Bank Accounts</Typography>
        {canManage && (
          <Button data-help="banking.accounts.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/banking/accounts/new')}>
            New Bank Account
          </Button>
        )}
      </Box>

      <Box data-help-section="banking.accounts.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField data-help="banking.accounts.search"
          label="Search"
          placeholder="Bank, account number or branch"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flex: '1 1 260px', maxWidth: 320 }}
        />
      </Box>

      {loading ? <CircularProgress /> : (
        <TableContainer data-help-section="banking.accounts.table" component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-help="banking.accounts.col.bank">Bank</TableCell>
                <TableCell data-help="banking.accounts.col.number">Account #</TableCell>
                <TableCell data-help="banking.accounts.col.branch">Branch</TableCell>
                <TableCell data-help="banking.accounts.col.gl">GL Account</TableCell>
                <TableCell data-help="banking.accounts.col.type">Type</TableCell>
                <TableCell data-help="banking.accounts.col.od" align="right">OD Limit</TableCell>
                <TableCell data-help="banking.accounts.col.actions" />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(row => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  data-testid={`bank-account-row-${row.id}`}
                  onClick={() => navigate(`/banking/accounts/${row.id}`)}
                >
                  <TableCell>{row.bankName}</TableCell>
                  <TableCell>{row.accountNumber}</TableCell>
                  <TableCell>{row.branchName ?? '—'}</TableCell>
                  <TableCell>
                    {row.glCashAccountCode ? `${row.glCashAccountCode} - ${row.glCashAccountName}` : '—'}
                  </TableCell>
                  <TableCell>{row.accountType ? humanizeEnum(row.accountType) : '—'}</TableCell>
                  <TableCell align="right">{row.overdraftLimit ? formatMoney(row.overdraftLimit) : '—'}</TableCell>
                  <TableCell align="right">
                    {canManage && (
                      <Button data-help="banking.accounts.action.edit" size="small" data-testid={`bank-account-edit-${row.id}`}
                        onClick={e => { e.stopPropagation(); navigate(`/banking/accounts/${row.id}/edit`); }}>
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center">No bank accounts found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default BankAccountList;
